import React from 'react';
import { Box, Typography, Container, Grid, Card } from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ImageIcon from '@mui/icons-material/Image';
import ReceiptIcon from '@mui/icons-material/Receipt';

const PageWrapper = styled(Box)({
  minHeight: '100vh',
  background: '#0a1628',
});

const HeroSection = styled(Box)({
  padding: '100px 0 60px',
  textAlign: 'center',
  background: 'linear-gradient(180deg, #0a1628 0%, #0f172a 100%)',
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
});

const SectionTitle = styled(Typography)({
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#fff',
  marginBottom: '24px',
});

const DocCard = styled(Card)({
  background: 'rgba(255, 255, 255, 0.03)',
  borderRadius: '16px',
  padding: '28px',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  height: '100%',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.06)',
    borderColor: 'rgba(59, 130, 246, 0.4)',
    transform: 'translateY(-4px)',
  },
});

const IconBox = styled(Box)(({ color }) => ({
  width: 48,
  height: 48,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `${color}15`,
  marginBottom: '16px',
  '& svg': {
    fontSize: 24,
    color: color,
  },
}));

const QuickLinkCard = styled(Box)({
  background: 'rgba(255, 255, 255, 0.02)',
  borderRadius: '12px',
  padding: '20px',
  border: '1px solid rgba(255, 255, 255, 0.05)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(59, 130, 246, 0.1)',
    borderColor: 'rgba(59, 130, 246, 0.3)',
  },
});

const Docs = () => {
  const mainDocs = [
    {
      icon: <RocketLaunchIcon />,
      color: '#3b82f6',
      title: 'Quick Start Guide',
      description: 'Get up and running in minutes. Learn the basics of patient registration, scheduling, and viewing studies.',
    },
    {
      icon: <MenuBookIcon />,
      color: '#8b5cf6',
      title: 'User Guide',
      description: 'Complete guide for sonographers and staff. Covers daily workflows, patient management, and reporting.',
    },
    {
      icon: <AdminPanelSettingsIcon />,
      color: '#10b981',
      title: 'Admin Manual',
      description: 'System administration guide. User management, branch settings, exam configuration, and billing setup.',
    },
    {
      icon: <PlayCircleOutlineIcon />,
      color: '#f59e0b',
      title: 'Video Tutorials',
      description: 'Step-by-step video walkthroughs for common tasks. Visual learning for all skill levels.',
    },
    {
      icon: <SettingsIcon />,
      color: '#06b6d4',
      title: 'DICOM Setup',
      description: 'Configure your ultrasound machines to send studies directly to Voxxil via DICOM protocol.',
    },
    {
      icon: <HelpOutlineIcon />,
      color: '#ec4899',
      title: 'FAQ & Troubleshooting',
      description: 'Common questions answered. Solutions to frequent issues and best practices.',
    },
  ];

  const quickLinks = [
    { icon: <PersonIcon />, title: 'Patient Registration', color: '#3b82f6' },
    { icon: <AssignmentIcon />, title: 'Creating Orders', color: '#8b5cf6' },
    { icon: <ImageIcon />, title: 'Viewing Studies', color: '#10b981' },
    { icon: <ReceiptIcon />, title: 'Generating Reports', color: '#f59e0b' },
  ];

  return (
    <PageWrapper>
      <HeroSection>
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 800,
              color: '#fff',
              mb: 2,
            }}
          >
            <GradientText>Documentation</GradientText>
          </Typography>
          <Typography
            sx={{
              color: '#94a3b8',
              fontSize: '1.1rem',
              maxWidth: 500,
              mx: 'auto',
            }}
          >
            Everything you need to get started with Voxxil RIS
          </Typography>
        </Container>
      </HeroSection>

      <Box sx={{ background: '#0a1628', pb: 8 }}>
        <Container maxWidth="lg">
          {/* Quick Links */}
          <Box sx={{ mb: 6 }}>
            <Typography sx={{ color: '#64748b', fontSize: '0.875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
              Quick Links
            </Typography>
            <Grid container spacing={2}>
              {quickLinks.map((link, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <QuickLinkCard>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ color: link.color }}>{link.icon}</Box>
                      <Typography sx={{ color: '#e2e8f0', fontSize: '0.9rem', fontWeight: 500 }}>
                        {link.title}
                      </Typography>
                    </Box>
                  </QuickLinkCard>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Main Documentation */}
          <SectionTitle>Guides & Tutorials</SectionTitle>
          <Grid container spacing={3}>
            {mainDocs.map((doc, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <DocCard>
                  <IconBox color={doc.color}>
                    {doc.icon}
                  </IconBox>
                  <Typography
                    sx={{
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      mb: 1,
                    }}
                  >
                    {doc.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.9rem',
                      lineHeight: 1.6,
                    }}
                  >
                    {doc.description}
                  </Typography>
                </DocCard>
              </Grid>
            ))}
          </Grid>

          {/* System Info */}
          <Box sx={{ mt: 8, p: 4, background: 'rgba(255,255,255,0.02)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
            <SectionTitle sx={{ mb: 3 }}>System Information</SectionTitle>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography sx={{ color: '#64748b', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', mb: 1 }}>
                  Default Login Credentials
                </Typography>
                <Box sx={{ background: 'rgba(0,0,0,0.3)', p: 2, borderRadius: '8px', fontFamily: 'monospace' }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem', mb: 0.5 }}>
                    Admin: <span style={{ color: '#60a5fa' }}>admin@voxxil.gy</span> / <span style={{ color: '#10b981' }}>Admin@123</span>
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem', mb: 0.5 }}>
                    Sonographer: <span style={{ color: '#60a5fa' }}>sonographer@voxxil.gy</span> / <span style={{ color: '#10b981' }}>Sono@123</span>
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                    Radiologist: <span style={{ color: '#60a5fa' }}>radiologist@voxxil.gy</span> / <span style={{ color: '#10b981' }}>Rad@123</span>
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ color: '#64748b', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', mb: 1 }}>
                  Service URLs (Development)
                </Typography>
                <Box sx={{ background: 'rgba(0,0,0,0.3)', p: 2, borderRadius: '8px', fontFamily: 'monospace' }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem', mb: 0.5 }}>
                    Application: <span style={{ color: '#60a5fa' }}>http://localhost:3000</span>
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem', mb: 0.5 }}>
                    OHIF Viewer: <span style={{ color: '#60a5fa' }}>http://localhost:3001</span>
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                    Orthanc DICOM: <span style={{ color: '#60a5fa' }}>http://localhost:8042</span>
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </PageWrapper>
  );
};

export default Docs;
